import React from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { LENS_MODELS } from "@/lib/lenses";

export default function PatientCard({ patient, setPatient }) {
  const upd = (k) => (e) => {
    const v = e?.target?.value ?? e;
    setPatient((p) => ({ ...p, [k]: v }));
  };

  const onLensChange = (id) => {
    const m = LENS_MODELS.find((l) => l.id === id);
    setPatient((p) => ({
      ...p,
      lens_id: id,
      a_constant: m?.a_constant ?? p.a_constant,
      a_constant_other: m?.a_constant_other ?? m?.a_constant ?? p.a_constant_other,
      hoffer_pacd: m?.hoffer_pacd ?? "",
      c_constant: m?.c_constant ?? 0.5,
    }));
  };

  const derivedPacd = (0.58357 * (parseFloat(patient.a_constant_other) || parseFloat(patient.a_constant) || 119.84) - 63.896).toFixed(3);

  const grouped = LENS_MODELS.reduce((acc, l) => {
    (acc[l.group] ||= []).push(l);
    return acc;
  }, {});

  return (
    <Card className="p-5 sm:p-6 border-slate-200">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h2 className="font-serif text-2xl font-semibold tracking-tight">Patient & Surgical Plan</h2>
          <p className="text-sm text-slate-500 mt-1">
            Confirm patient information and refractive history before calculation.
          </p>
        </div>
        <div className="hidden sm:block text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Step 02
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Field label="Patient name" span="col-span-2">
          <Input value={patient.patient_name} onChange={upd("patient_name")}
                 placeholder="e.g. R. Sharma" data-testid="patient-name-input" />
        </Field>
        <Field label="Patient ID">
          <Input value={patient.patient_id} onChange={upd("patient_id")}
                 placeholder="MRN" data-testid="patient-id-input" />
        </Field>
        <Field label="Age">
          <Input value={patient.age} onChange={upd("age")} type="number"
                 placeholder="yrs" data-testid="patient-age-input" />
        </Field>
        <Field label="Prior corneal surgery" span="col-span-2">
          <Select value={patient.prior_surgery} onValueChange={upd("prior_surgery")}>
            <SelectTrigger data-testid="prior-surgery-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="lasik_myopic">Prior Myopic LASIK/PRK</SelectItem>
              <SelectItem value="lasik_hyperopic">Prior Hyperopic LASIK</SelectItem>
              <SelectItem value="rk">Prior RK (Radial Keratotomy)</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="IOL model (lens library)" span="col-span-3">
          <Select value={patient.lens_id || "custom"} onValueChange={onLensChange}>
            <SelectTrigger data-testid="lens-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-80">
              {Object.entries(grouped).map(([group, list]) => (
                <SelectGroup key={group}>
                  <SelectLabel className="text-[10px] uppercase tracking-wider text-slate-500">{group}</SelectLabel>
                  {list.map((l) => (
                    <SelectItem key={l.id} value={l.id} data-testid={`lens-option-${l.id}`}>
                      <span className="mr-2">{l.name}</span>
                      {l.a_constant !== null && (
                        <span className="text-xs text-slate-500 font-mono">A {l.a_constant}</span>
                      )}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Target refraction (D)">
          <Input type="number" step="0.25" value={patient.target_refraction}
                 onChange={upd("target_refraction")} data-testid="target-refraction-input" />
        </Field>
        <Field label="A-constant (Barrett)">
          <Input type="number" step="0.01" value={patient.a_constant}
                 onChange={upd("a_constant")} data-testid="a-constant-input" />
        </Field>
        <Field label="A-const (SRK/T · Haigis · Kane)">
          <Input type="number" step="0.01" value={patient.a_constant_other ?? ""}
                 onChange={upd("a_constant_other")} data-testid="a-constant-other-input" />
        </Field>
        <Field label="Hoffer Q pACD">
          <Input type="number" step="0.001" value={patient.hoffer_pacd ?? ""}
                 placeholder={`derived ${derivedPacd}`}
                 onChange={upd("hoffer_pacd")} data-testid="hoffer-pacd-input" />
        </Field>
        <Field label="ELP C-constant (× LT)">
          <Input type="number" step="0.01" min="0" max="1" value={patient.c_constant ?? 0.5}
                 onChange={upd("c_constant")} data-testid="c-constant-input" />
        </Field>
        <Field label="SIA (D)">
          <Input type="number" step="0.05" value={patient.sia}
                 onChange={upd("sia")} data-testid="sia-input" />
        </Field>
        <Field label="SIA axis (°)">
          <Input type="number" step="1" value={patient.sia_axis}
                 onChange={upd("sia_axis")} data-testid="sia-axis-input" />
        </Field>
        <Field label="Incision">
          <div className="h-10 rounded-md border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-600 truncate">
            Temporal @ <span className="font-mono ml-1">{patient.sia_axis}°</span>
          </div>
        </Field>
      </div>
      <p className="text-[11px] text-slate-500 mt-3">
        Barrett uses its own A-constant; SRK/T, Haigis, Holladay 1 and Kane use the second A-constant. Leave Hoffer Q pACD
        empty to derive it from that A-constant, or enter the manufacturer value (e.g. 6.095 for Cristalens Artis).
        Barrett / Kane ELP is the mean of the K-AL estimate and the anatomical estimate ACD + C × LT. C is a
        lens-specific constant (0.50 nominal); once ≥3 post-op refractions are logged for a lens, the surgeon-factor
        panel offers a learned C for that lens.
      </p>
    </Card>
  );
}

function Field({ label, span = "", children }) {
  return (
    <div className={span}>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
